import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { RichTextEditor } from '@/design-system/atoms/RichTextEditor';

import Button from '../../atoms/Button/Button';
import styles from './ExitSurvey.module.scss';

// Utility functions for cookies
function setCookie(name, value, days = 30) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
}

function getCookie(name) {
    return document.cookie
        .split('; ')
        .find((row) => row.startsWith(name + '='))
        ?.split('=')[1];
}

const ExitSurvey = ({
    surveyUrl,
    exitSurveyContext,
    surveyButtonText,
    cancelButtonText,
    surveyExpire,
    image,
    scrollThreshold,
    maxMobileWidth,
    maxOverallWidth,
}) => {
    const [showSurvey, setShowSurvey] = useState(false);
    const [cookie, setCookieState] = useState(null);
    const [showMobile, setShowMobile] = useState(false);
    const [showMobileMarker, setShowMobileMarker] = useState(null);
    const [showIframe, setShowIframe] = useState(false);
    const SCREEN_BREAKPOINT = 809; // px

    // Check for cookie on mount
    useEffect(() => {
        const storedCookie = getCookie('ahs-eis');
        if (storedCookie) {
            setCookieState(storedCookie);
        }
    }, []);

    useEffect(() => {
        const handleMouseOut = (e) => {
            if (!cookie && !e.toElement && !e.relatedTarget) {
                setShowSurvey(true);
            }
        };

        const handleScroll = () => {
            if (!cookie) {
                let lastPosition = window.scrollY;
                setTimeout(() => {
                    if (lastPosition >= scrollThreshold) {
                        setShowMobile(true);
                        setShowMobileMarker(lastPosition);
                    }
                    if (showMobile && lastPosition < showMobileMarker && showSurvey === false) {
                        setShowSurvey(true);
                    }
                }, 500);
            }
        };
        const isTouchDevice = window.matchMedia(`(max-width: ${SCREEN_BREAKPOINT}px)`).matches;

        if (!cookie) {
            if (isTouchDevice) {
                document.addEventListener('scroll', handleScroll);
            } else {
                document.addEventListener('mouseout', handleMouseOut);
            }
        }

        return () => {
            document.removeEventListener('mouseout', handleMouseOut);
            document.removeEventListener('scroll', handleScroll);
        };
    }, [cookie, surveyUrl, scrollThreshold, showMobile, showSurvey, showMobileMarker]);

    const handleContinue = () => {
        setCookie('ahs-eis', surveyUrl, surveyExpire);
        setCookieState(surveyUrl);
        if (window.matchMedia('(max-width: 803px)').matches) {
            window.open(surveyUrl, '_blank');
            setShowSurvey(false);
        } else {
            setShowIframe(true);
        }
    };

    const handleCancel = () => {
        setCookie('ahs-eis', surveyUrl, surveyExpire);
        setCookieState(surveyUrl);
        setShowSurvey(false);
    };

    if (!showSurvey) {
        return null;
    }

    return (
        <div data-id={'exit-survey'} className={styles['exit-survey']}>
            <div
                className={styles['modal']}
                style={
                    showIframe
                        ? {
                              width: maxMobileWidth || '70%',
                              maxWidth: maxOverallWidth || '1440px',
                          }
                        : undefined
                }
            >
                <div className={styles['modal-header']}>
                    <Button
                        ariaLabel={'Close Survey Modal'}
                        className={styles['close-button']}
                        buttonStyle={Button.STYLE.TERTIARY}
                        iconName="x"
                        onClick={handleCancel}
                    />
                </div>
                <div
                    className={styles['modal-content-with-bg']}
                    style={
                        image?.src
                            ? {
                                  backgroundImage: `url(${image.src})`,
                              }
                            : undefined
                    }
                >
                    {!showIframe && (
                        <>
                            <div className={styles['modal-body']}>
                                {exitSurveyContext && <RichTextEditor text={exitSurveyContext} />}
                            </div>
                            <div className={styles['modal-footer']}>
                                <Button
                                    className={styles['continue-button']}
                                    buttonStyle={Button.STYLE.PRIMARY}
                                    onClick={handleContinue}
                                    label={surveyButtonText}
                                />
                                <Button
                                    className={styles['cancel-button']}
                                    buttonStyle={Button.STYLE.SECONDARY}
                                    onClick={handleCancel}
                                    label={cancelButtonText}
                                />
                            </div>
                        </>
                    )}

                    {showIframe && (
                        <div className={styles['modal-body']}>
                            <iframe
                                src={surveyUrl}
                                title="Exit Survey"
                                width="100%"
                                height="500px"
                                style={{ border: 'none' }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

ExitSurvey.propTypes = {
    surveyUrl: PropTypes.string.isRequired,
    surveyMessage: PropTypes.string,
    surveyButtonText: PropTypes.string,
    cancelButtonText: PropTypes.string,
    surveyExpire: PropTypes.number,
};

export default ExitSurvey;
ExitSurvey.defaultProps = {
    surveyMessage: 'Help us improve the website for people like you!',
    surveyButtonText: 'Start the Survey',
    cancelButtonText: 'No Thanks',
    surveyExpire: 30, // Default to 30 days
};
ExitSurvey.displayName = 'Exit-Survey';
